import React, { useMemo } from "react";
import { alphaFromHex } from "../../util/colors";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";

export default function DivisionHeader({ division, activeSeasonData }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const styles = useMemo(() => {
        return {
            card: {
                position: "relative",
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
                background: "#fff",
            },
            header: {
                padding: "14px 14px 12px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 240,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                textTransform: "uppercase",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 18,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            subtitle: {
                margin: 0,
                marginTop: 4,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            body: {
                padding: 14,
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
            },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                minWidth: 200,
                flex: "1 1 220px",
            },
            chipLabel: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                color: "rgba(20,21,26,0.60)",
                textTransform: "uppercase",
                margin: 0,
                lineHeight: 1,
            },
            chipValue: {
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
                margin: 0,
                lineHeight: 1.1,
            },
            dot: (active) => ({
                width: 10,
                height: 10,
                borderRadius: 999,
                background: active
                    ? "rgba(46,204,113,0.95)"
                    : "rgba(231,76,60,0.95)",
                boxShadow: active
                    ? "0 0 0 4px rgba(46,204,113,0.15)"
                    : "0 0 0 4px rgba(231,76,60,0.15)",
            }),
            statusText: {
                fontSize: 12.5,
                fontWeight: 800,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                margin: 0,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    return (
        <div className="row">
            <div className="col-12">
                <div style={styles.card}>

                    {/* Header */}
                    <div style={styles.header}>
                        <div>
                            <div style={styles.titleWrap}>
                                <span style={styles.badge}>
                                    {t("divisionHeader.badge")}
                                </span>

                                <h2 style={styles.title}>
                                    {division?.name || t("divisionHeader.empty")}
                                </h2>
                            </div>

                            <p style={styles.subtitle}>
                                {t("divisionHeader.subtitle")}
                            </p>
                        </div>

                        {/* Status pill */}
                        <p style={styles.statusText}>
                            <span style={styles.dot(!!division?.isOpen)} />
                            {division?.isOpen
                                ? t("divisionHeader.open")
                                : t("divisionHeader.closed")}
                        </p>
                    </div>

                    {/* Chips */}
                    <div style={styles.body}>
                        <div style={styles.chip}>
                            <div>
                                <p style={styles.chipLabel}>
                                    {t("divisionHeader.sport")}
                                </p>

                                <p style={styles.chipValue}>
                                    {division?.sport?.name || t("divisionHeader.empty")}
                                </p>
                            </div>
                        </div>

                        <div style={styles.chip}>
                            <div>
                                <p style={styles.chipLabel}>
                                    {t("divisionHeader.season")}
                                </p>

                                <p style={styles.chipValue}>
                                    {activeSeasonData?.name || t("divisionHeader.empty")}
                                </p>
                            </div>
                        </div>

                        <div style={styles.chip}>
                            <div>
                                <p style={styles.chipLabel}>
                                    {t("divisionHeader.divisionStatus")}
                                </p>

                                <p style={styles.chipValue}>
                                    {division?.isOpen
                                        ? t("divisionHeader.active")
                                        : t("divisionHeader.archived")}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}